/**
 * Live-program content diff classifier.
 *
 * Classifies an edit to a day's content as "safe" (can be applied in place to
 * all enrolled users) or "structural" (forces a new program version). User
 * responses are keyed by section/question position, so any change that shifts
 * those positions or changes the meaning of a slot is structural.
 *
 * Moved here from shared/content-types.ts; only consumed by StructuredDayEditor.
 */
import type { DayContent, JournalReference } from "@shared/content-types";

export interface ProgramDaySnapshot {
  id?: string;
  dayNumber: number;
  content: DayContent | null | undefined;
}

export type ContentDiffResult =
  | { safe: true; reasons: [] }
  | { safe: false; reasons: string[] };

export interface ClassifyOptions {
  /**
   * Map of dayNumber -> Set of section indexes that are pointed at by some
   * other day's journalReference. Removing or reordering one of those is
   * structural even when it would otherwise look safe.
   */
  referencedSections?: Map<number, Set<number>>;
  /**
   * The dayNumber of the content being diffed. Required when
   * `referencedSections` is supplied.
   */
  dayNumber?: number;
}

// ===========================================================================
// Private helpers
// ===========================================================================

const SAFE_STRING_KEYS = new Set([
  "title",
  "description",
  "prompt",
  "intro",
  "focus",
  "focusNote",
  "quote",
  "content",
  "subDescription",
  "video",
  "videoUrl",
  "image",
  "note",
  "duration",
  "exercise",
  "powerWords",
  "label",
  "text",
  "author",
  "term",
  "tipsTitle",
  "levelsTitle",
  "scaleTitle",
  "checkboxText",
  "expandable",
  "secondExpandable",
  "sampleResponse",
  "additionalContent",
  "overview",
]);

const STRUCTURAL_ARRAY_KEYS = new Set([
  "sections",
  "exercises",
  "questions",
  "introQuestions",
  "steps",
  "expandables",
  "definitions",
  "levels",
  "examples",
  "archetypes",
  "resources",
  "categories",
  "salesProcessSteps",
  "objectives",
]);

const TEXT_ARRAY_KEYS = new Set([
  "bullets",
  "tips",
  "doList",
  "dontList",
  "phrases",
]);

const BOOKMARK_KEYS = new Set(["journalReference", "journalReferences"]);

const ITEM_IDENTITY_KEYS = new Set([
  "type",
  "category",
  "points",
  "kind",
  "number",
]);

const RESPONSE_BEARING_ARRAYS = new Set(["sections", "questions", "exercises", "introQuestions"]);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function sectionLabel(item: unknown, idx: number): string | null {
  if (!isPlainObject(item)) return null;
  const t = typeof item.title === "string" ? item.title : null;
  const c = typeof item.category === "string" ? item.category : null;
  return t || c || `index ${idx}`;
}

function sectionsLookEquivalent(a: unknown, b: unknown): boolean {
  if (!isPlainObject(a) || !isPlainObject(b)) return a === b;
  return (
    a.title === b.title &&
    a.category === b.category &&
    (a.prompt ?? null) === (b.prompt ?? null)
  );
}

function itemFingerprint(item: unknown): string {
  if (!isPlainObject(item)) {
    return typeof item === "string" || typeof item === "number" || typeof item === "boolean"
      ? `prim:${String(item)}`
      : "prim:other";
  }
  const fpKeys = ["id", "type", "category", "kind", "number", "points", "title", "prompt", "content", "label", "text"];
  const fp: Record<string, unknown> = {};
  for (const k of fpKeys) {
    if (item[k] !== undefined) fp[k] = item[k];
  }
  return JSON.stringify(fp);
}

function diffNode(
  oldNode: unknown,
  newNode: unknown,
  path: string,
  reasons: string[],
  ctx: { dayNumber?: number; referenced?: Set<number> },
): void {
  if (oldNode === newNode) return;
  if (oldNode === undefined || oldNode === null) return;
  if (newNode === undefined || newNode === null) {
    reasons.push(`Removed ${path || "root"}`);
    return;
  }
  if (typeof oldNode !== typeof newNode || Array.isArray(oldNode) !== Array.isArray(newNode)) {
    reasons.push(`Type changed at ${path || "root"}`);
    return;
  }
  if (Array.isArray(oldNode) && Array.isArray(newNode)) {
    diffArray(oldNode, newNode, path, reasons, ctx);
    return;
  }
  if (isPlainObject(oldNode) && isPlainObject(newNode)) {
    diffObject(oldNode, newNode, path, reasons, ctx);
    return;
  }
  reasons.push(`Value changed at ${path}`);
}

function diffArray(
  oldArr: unknown[],
  newArr: unknown[],
  path: string,
  reasons: string[],
  ctx: { dayNumber?: number; referenced?: Set<number> },
): void {
  const minLen = Math.min(oldArr.length, newArr.length);
  for (let i = 0; i < minLen; i++) {
    diffNode(oldArr[i], newArr[i], `${path}[${i}]`, reasons, ctx);
  }
  if (newArr.length < oldArr.length) {
    reasons.push(`Removed ${oldArr.length - newArr.length} item(s) from ${path}`);
  }
}

function diffStructuralArray(
  oldArr: unknown[],
  newArr: unknown[],
  path: string,
  reasons: string[],
  ctx: { dayNumber?: number; referenced?: Set<number> },
  isSections: boolean,
  parentKey?: string,
): void {
  if (newArr.length < oldArr.length) {
    for (let i = newArr.length; i < oldArr.length; i++) {
      const what = sectionLabel(oldArr[i], i);
      reasons.push(`Removed ${path}[${i}]${what ? ` (${what})` : ""}`);
    }
  }

  const compareLen = Math.min(oldArr.length, newArr.length);

  const oldFps = oldArr.slice(0, compareLen).map(itemFingerprint);
  const newFps = newArr.slice(0, compareLen).map(itemFingerprint);
  const oldFpIndex = new Map<string, number[]>();
  for (let i = 0; i < oldFps.length; i++) {
    const list = oldFpIndex.get(oldFps[i]) ?? [];
    list.push(i);
    oldFpIndex.set(oldFps[i], list);
  }
  const flaggedReorder = new Set<number>();
  for (let i = 0; i < compareLen; i++) {
    if (newFps[i] === oldFps[i]) continue;
    const otherPositions = (oldFpIndex.get(newFps[i]) ?? []).filter((j) => j !== i);
    if (otherPositions.length > 0) {
      flaggedReorder.add(i);
      const what = sectionLabel(newArr[i], i) || `index ${i}`;
      reasons.push(
        `Reordered ${path}[${i}] (${what}) — was at index ${otherPositions[0]} previously`,
      );
    }
  }

  for (let i = 0; i < compareLen; i++) {
    if (flaggedReorder.has(i)) continue;

    const oldItem = oldArr[i];
    const newItem = newArr[i];
    if (
      isPlainObject(oldItem) &&
      isPlainObject(newItem) &&
      parentKey &&
      RESPONSE_BEARING_ARRAYS.has(parentKey)
    ) {
      // "title" is a cosmetic label — renaming it is safe and does not affect
      // the server's structural signature. Only prompt/label renames matter
      // because they can break back-reference expandables that key off prompt text.
      for (const key of ["prompt", "label"] as const) {
        const ov = oldItem[key];
        const nv = newItem[key];
        if (typeof ov === "string" && typeof nv === "string" && ov !== nv) {
          reasons.push(
            `Renamed ${key} of ${path}[${i}] (was ${JSON.stringify(ov)}, now ${JSON.stringify(nv)})`,
          );
        }
      }
    }

    if (
      isSections &&
      ctx.referenced &&
      ctx.referenced.has(i) &&
      !sectionsLookEquivalent(oldArr[i], newArr[i])
    ) {
      reasons.push(
        `Section ${path}[${i}] is referenced by another day's bookmark — structural change required.`,
      );
    }
    diffNode(oldArr[i], newArr[i], `${path}[${i}]`, reasons, ctx);
  }
}

function diffObject(
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>,
  path: string,
  reasons: string[],
  ctx: { dayNumber?: number; referenced?: Set<number> },
): void {
  const keys = Array.from(new Set<string>([...Object.keys(oldObj), ...Object.keys(newObj)]));

  for (const key of keys) {
    const subPath = path ? `${path}.${key}` : key;
    const oldVal = oldObj[key];
    const newVal = newObj[key];

    if (oldVal === newVal) continue;

    if (BOOKMARK_KEYS.has(key)) continue;

    if (SAFE_STRING_KEYS.has(key)) {
      if (
        (typeof oldVal === "string" || oldVal === undefined || oldVal === null) &&
        (typeof newVal === "string" || newVal === undefined || newVal === null)
      ) {
        continue;
      }
      if (oldVal !== undefined && newVal !== undefined && typeof oldVal !== typeof newVal) {
        reasons.push(`Type changed at ${subPath}`);
        continue;
      }
    }

    if (ITEM_IDENTITY_KEYS.has(key) && oldVal !== undefined && newVal !== undefined) {
      reasons.push(`Changed ${key} at ${subPath} (was ${JSON.stringify(oldVal)}, now ${JSON.stringify(newVal)})`);
      continue;
    }

    if (TEXT_ARRAY_KEYS.has(key)) continue;

    if (STRUCTURAL_ARRAY_KEYS.has(key)) {
      const oldArr = Array.isArray(oldVal) ? oldVal : [];
      const newArr = Array.isArray(newVal) ? newVal : [];
      diffStructuralArray(oldArr, newArr, subPath, reasons, ctx, key === "sections", key);
      continue;
    }

    diffNode(oldVal, newVal, subPath, reasons, ctx);
  }
}

// ===========================================================================
// Public API
// ===========================================================================

/**
 * Classify the difference between two DayContent payloads as safe or
 * structural. Used by the live-program guardrail in StructuredDayEditor.
 */
export function classifyContentDiff(
  oldContent: DayContent | null | undefined,
  newContent: DayContent | null | undefined,
  opts: ClassifyOptions = {},
): ContentDiffResult {
  const reasons: string[] = [];
  const ctx = {
    dayNumber: opts.dayNumber,
    referenced:
      opts.dayNumber !== undefined
        ? opts.referencedSections?.get(opts.dayNumber)
        : undefined,
  };
  diffNode(oldContent ?? {}, newContent ?? {}, "", reasons, ctx);
  if (reasons.length === 0) {
    return { safe: true, reasons: [] };
  }
  const unique = Array.from(new Set(reasons));
  return { safe: false, reasons: unique };
}

function readBookmarks(section: unknown): JournalReference[] {
  if (!isPlainObject(section)) return [];
  const refs: JournalReference[] = [];
  const single = section.journalReference;
  if (isPlainObject(single)) refs.push(single as unknown as JournalReference);
  const list = section.journalReferences;
  if (Array.isArray(list)) {
    for (const r of list) if (isPlainObject(r)) refs.push(r as unknown as JournalReference);
  }
  return refs;
}

function readSectionsFromContent(content: DayContent | null | undefined): unknown[] {
  if (!isPlainObject(content)) return [];
  const arr = (content as Record<string, unknown>).sections;
  return Array.isArray(arr) ? arr : [];
}

/**
 * Build the `referencedSections` map. Each day -> set of section indexes that
 * are targets of some other day's bookmark, so that removing or restructuring
 * one of those sections is caught as a structural edit.
 */
export function buildReferencedSectionsMap(days: ProgramDaySnapshot[]): Map<number, Set<number>> {
  const map = new Map<number, Set<number>>();
  for (const day of days) {
    const sections = readSectionsFromContent(day.content);
    sections.forEach((section) => {
      const refs = readBookmarks(section);
      for (const ref of refs) {
        if (typeof ref.dayNumber !== "number" || typeof ref.sectionIndex !== "number") continue;
        if (!map.has(ref.dayNumber)) map.set(ref.dayNumber, new Set());
        map.get(ref.dayNumber)!.add(ref.sectionIndex);
      }
    });
  }
  return map;
}
